<script setup>
import { computed, defineEmits, defineProps, onMounted, ref } from 'vue';
import { $api } from '@/services';
import fileIcon from '@/assets/images/screening/file-earmark-plus.svg';
import closeIcon from '@/assets/images/screening/close-x.svg';
import ScreeningScaleList from '@/views/screening/features/ScreeningScaleList.vue';

const props = defineProps({
	studentOnly: {
		type: Boolean,
		default: false,
	},
});

const emit = defineEmits(['close', 'continue']);

const step = ref(1);
const students = ref([]);
const scales = ref([]);
const query = ref('');
const selectedStudentId = ref(null);
const selectedScaleId = ref(null);
const studentOpen = ref(false);
const loading = ref(true);

const unwrapList = (body) => {
	if (Array.isArray(body)) return body;
	return body?.data || [];
};

const fold = (value) =>
	(value || '')
		.toString()
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/đ/g, 'd');

const filteredStudents = computed(() => {
	const keyword = fold(query.value);
	if (!keyword) return students.value;
	return students.value.filter((item) => fold(item.name).includes(keyword));
});

const selectedStudent = computed(() =>
	students.value.find((item) => item.id === selectedStudentId.value)
);

const canSelect = (scale) =>
	scale?.state === 'completed' || scale?.state === 'published';

const selectedScale = computed(() =>
	scales.value.find(
		(item) => item.id === selectedScaleId.value && canSelect(item)
	)
);

const loadStudents = async () => {
	try {
		const res = await $api.student.getOrgSimpleStudents();
		students.value = unwrapList(res?.data);
		return;
	} catch (err) {
		students.value = [];
	}
	try {
		const res = await $api.student.getSimpleStudents();
		students.value = unwrapList(res?.data);
	} catch (err) {
		students.value = [];
	}
};

const loadScales = async () => {
	try {
		const res = await $api.screening.scales();
		scales.value = unwrapList(res?.data);
		const first = scales.value.find((item) => canSelect(item));
		selectedScaleId.value = first ? first.id : null;
	} catch (err) {
		scales.value = [];
	}
};

onMounted(async () => {
	loading.value = true;
	if (props.studentOnly) {
		await loadStudents();
	} else {
		await Promise.all([loadStudents(), loadScales()]);
	}
	loading.value = false;
});

const next = () => {
	if (step.value === 1) {
		if (!selectedStudent.value) return;
		if (props.studentOnly) {
			emit('continue', { student: selectedStudent.value });
			return;
		}
		step.value = 2;
		return;
	}
	if (!selectedScale.value) return;
	emit('continue', {
		student: selectedStudent.value,
		scale: selectedScale.value,
	});
};
</script>

<template>
	<div class="fixed inset-0 z-50 flex justify-end" @click.self="emit('close')">
		<div class="absolute inset-0 bg-black bg-opacity-20" @click="emit('close')"></div>
		<aside
			class="relative flex flex-col h-full w-full bg-white shadow-xl screening-create"
		>
			<div
				class="flex items-center justify-between flex-shrink-0 screening-create__header"
			>
				<div class="flex items-center screening-create__title">
					<img :src="fileIcon" alt="" width="24" height="24" />
					Tạo mới
				</div>
				<button type="button" class="screening-create__close" @click="emit('close')">
					<img :src="closeIcon" alt="" width="24" height="24" />
				</button>
			</div>
			<div class="flex-1 min-h-0 overflow-y-auto px-6 py-6">
				<div v-if="loading" class="text-sm text-neutral-400">Đang tải...</div>
				<div v-else-if="step === 1">
					<div class="text-neutral-500 mb-6">Chọn học sinh để tiếp tục</div>
					<div class="text-sm font-semibold text-neutral-600 mb-1">
						Chọn học sinh<span class="text-sienna-500">*</span>
					</div>
					<button
						type="button"
						class="screening-select"
						@click="studentOpen = !studentOpen"
					>
						<span :class="selectedStudent ? 'text-neutral-700' : 'text-neutral-400'">
							{{ selectedStudent ? selectedStudent.name : 'Tìm theo tên học sinh' }}
						</span>
						<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
							<path d="M5 8l5 5 5-5" stroke="#94A3B8" stroke-width="1.6" stroke-linecap="round" />
						</svg>
					</button>
					<div v-if="studentOpen" class="screening-menu">
						<input
							v-model="query"
							type="text"
							class="screening-menu__search"
							placeholder="Tìm theo tên học sinh"
						/>
						<button
							v-for="student in filteredStudents"
							:key="student.id"
							type="button"
							class="screening-menu__item"
							@click="
								selectedStudentId = student.id;
								studentOpen = false;
							"
						>
							{{ student.name }}
						</button>
						<div
							v-if="filteredStudents.length === 0"
							class="px-4 py-3 text-sm text-neutral-400"
						>
							Không tìm thấy học sinh
						</div>
					</div>
				</div>
				<ScreeningScaleList
					v-else
					:scales="scales"
					:selected-id="selectedScaleId"
					@select="selectedScaleId = $event"
				/>
			</div>
			<div class="flex-shrink-0 screening-create__footer">
				<div class="flex items-center justify-end screening-create__actions">
					<button
						v-if="step === 2"
						type="button"
						class="screening-create__back"
						@click="step = 1"
					>
						Quay lại
					</button>
					<button
						type="button"
						class="screening-create__next"
						:disabled="
							loading ||
							(step === 1 && !selectedStudent) ||
							(step === 2 && !selectedScale)
						"
						@click="next"
					>
						Tiếp tục
					</button>
				</div>
			</div>
		</aside>
	</div>
</template>

<style lang="scss" scoped>
.screening-create {
	max-width: 512px;
}

.screening-create__header {
	background: #eef3f8;
	padding: 24px;
}

.screening-create__title {
	gap: 8px;
	color: #344256;
	font-size: 20px;
	font-weight: 600;
	line-height: 28px;
}

.screening-create__close {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
}

.screening-create__footer {
	border-top: 1px solid #faf5f1;
}

.screening-create__actions {
	gap: 16px;
	padding: 16px;
}

.screening-create__back,
.screening-create__next {
	padding: 12px 20px;
	border-radius: 4px;
	font-size: 14px;
	font-weight: 600;
	line-height: 20px;
}

.screening-create__back {
	color: #65758b;
}

.screening-create__next {
	color: #344256;
	background: #f0bb4f;
	box-shadow: 0 0 1px rgba(12, 26, 75, 0.24), 0 3px 8px rgba(50, 50, 71, 0.05);

	&:disabled {
		opacity: 0.5;
		cursor: default;
	}
}

.screening-select {
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	height: 52px;
	padding: 0 16px;
	border-radius: 4px;
	background: #fff;
	border: 0.5px solid #f8fafc;
	box-shadow: 0 0 1px rgba(50, 50, 71, 0.2), 0 1px 2px rgba(50, 50, 71, 0.08);
	font-size: 16px;
	text-align: left;
}

.screening-menu {
	margin-top: 8px;
	max-height: 280px;
	overflow-y: auto;
	background: #fff;
	border-radius: 4px;
	box-shadow: 0 0 1px rgba(12, 26, 75, 0.24), 0 3px 8px rgba(50, 50, 71, 0.05);
}

.screening-menu__search {
	width: 100%;
	padding: 12px 16px;
	border: 0;
	border-bottom: 1px solid #e2e8f0;
	outline: none;
	font-size: 14px;
}

.screening-menu__item {
	display: block;
	width: 100%;
	padding: 10px 16px;
	text-align: left;
	font-size: 14px;
	color: #344256;

	&:hover {
		background: #f8fafc;
	}
}

</style>
